<template>
  <div
    class="v2-public-wishlist"
    :class="{ 'v2-public-wishlist--branded': variant === 'branded' }"
  >
    <!--
      Standalone ambient mesh. The V2 glass design normally blurs/tints against
      the mesh that the PLG shell paints behind the iframe. A shared public link
      opens this page top-level on the B2B domain (no PLG parent), so we render
      our own mesh here — but only when standalone, so we never stack a second
      mesh when the page happens to be embedded in an iframe.
    -->
    <div
      v-if="showMesh && isPlain"
      class="v2-public-wishlist__mesh"
      aria-hidden="true"
    />
    <!--
      KP-4545: the gate opens the brand's lookbook when plg-go-be has a ready
      branded page for this token, and today's page (the slot) otherwise.
    -->
    <line-sheet-gate
      v-if="publicToken"
      :key="publicToken"
      :public-token="publicToken"
      :query="$route.query"
      @variant="onVariant"
    >
      <div class="v2-public-wishlist__content">
        <v2-wishlist-products :public-token="publicToken" />
      </div>
    </line-sheet-gate>

    <!-- KP-3289: V1 parity — cookie-consent banner (LayoutVertical renders this
         for the legacy public page; the v2 layout does not, so we add it here
         for the standalone public page). The branded page carries its own
         inline cookie row (KP-4545). -->
    <app-cookies v-if="isPlain" />
  </div>
</template>

<script>
import AppCookies from '@core/layouts/components/AppCookies.vue'
import { isInIframe } from '@/utils/iframe-detect'
import LineSheetViewTrackingMixin from '@/views/mixins/LineSheetViewTrackingMixin.vue'
import V2WishlistProducts from './V2WishlistProducts.vue'
import LineSheetGate from './branded/LineSheetGate.vue'

export default {
  name: 'V2PublicWishlist',
  components: {
    V2WishlistProducts,
    AppCookies,
    LineSheetGate,
  },
  mixins: [LineSheetViewTrackingMixin],
  data() {
    return {
      // Only paint our own mesh when this page is the top-level document
      // (a real shared public link). Inside an iframe the parent provides it.
      showMesh: !isInIframe(),
      previousFavicon: null,
      // pending | branded | plain | browse (KP-4545 LineSheetGate).
      variant: 'pending',
    }
  },
  computed: {
    // From the route, so a new token on the same page opens a fresh gate.
    publicToken() {
      return this.$route.params.publicToken
    },
    // Today's page: no branded presentation, or the buyer chose Browse all.
    isPlain() {
      return this.variant === 'plain' || this.variant === 'browse'
    },
  },
  mounted() {
    this.reportLineSheetVisit()
  },
  beforeDestroy() {
    this.restoreFavicon()
  },
  methods: {
    onVariant(variant) {
      this.variant = variant
      // KP-3289: show the V2 Kingpin mark in the browser tab for the standalone
      // public link, on the plain page only (the branded page shows the brand's
      // own). Inside the iframe this is a no-op — the tab favicon belongs to the
      // parent (PLG) document. Waits a tick so a lookbook leaving on this
      // change has put the original icon back before it is saved.
      this.$nextTick(() => {
        if (this.needsKingpinFavicon()) this.applyPublicFavicon()
      })
    },
    needsKingpinFavicon() {
      return this.showMesh && this.isPlain && !this.previousFavicon
    },
    applyPublicFavicon() {
      const link = document.querySelector("link[rel~='icon']")
      if (!link) return
      this.previousFavicon = {
        href: link.getAttribute('href'),
        type: link.getAttribute('type'),
      }
      link.setAttribute('type', 'image/svg+xml')
      // eslint-disable-next-line global-require
      link.setAttribute('href', require('@/assets/images/logo/logo-dark.svg'))
    },
    restoreFavicon() {
      if (!this.previousFavicon) return
      const link = document.querySelector("link[rel~='icon']")
      if (!link) return
      if (this.previousFavicon.type) {
        link.setAttribute('type', this.previousFavicon.type)
      } else {
        link.removeAttribute('type')
      }
      link.setAttribute('href', this.previousFavicon.href)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-public-wishlist {
  // KP-4545: the branded lookbook runs edge to edge; LayoutV2 pads its content.
  &--branded {
    margin: calc(var(--v2-space-6, 0px) * -1);
  }

  &__mesh {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    // Light base + soft violet/indigo/fuchsia blobs, mirroring the PLG shell
    // mesh so the glass surfaces read the same as the authenticated page.
    background-color: var(--v2-background);
    background-image:
      radial-gradient(60% 55% at 12% 0%, var(--v2-mesh-1-strong), transparent 70%),
      radial-gradient(50% 45% at 100% 8%, var(--v2-mesh-3), transparent 70%),
      radial-gradient(55% 50% at 50% 100%, var(--v2-mesh-2), transparent 72%);
  }

  // Keep the page content above the fixed mesh layer.
  &__content {
    position: relative;
    z-index: 1;
  }

  // KP-3289: restyle the shared cookie banner for the glass public page —
  // full-bleed, vertically centred content, glassmorphic surface, and a solid
  // violet Accept button (the default light button is invisible on a
  // translucent bar). Scoped so V1 / other pages are untouched.
  ::v-deep #banner {
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    background: rgba(255, 255, 255, 0.62) !important;
    backdrop-filter: blur(18px) saturate(150%);
    -webkit-backdrop-filter: blur(18px) saturate(150%);
    border-top: 1px solid var(--v2-glass-stroke-strong);
    box-shadow: 0 -8px 24px rgba(26, 10, 62, 0.08);
    color: var(--v2-foreground) !important;
    opacity: 1 !important;

    #cookies {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: var(--v2-space-2) var(--v2-space-3);
      min-height: 64px;
      margin: 0;
      padding: var(--v2-space-2) var(--v2-space-4);
      font-family: var(--v2-font-sans);
      color: var(--v2-foreground);

      a {
        color: var(--v2-primary);
        font-weight: 600;
      }

      button {
        margin: 0;
        background: var(--v2-primary);
        border-color: var(--v2-primary);
        color: #fff;
        font-weight: 600;
      }
    }
  }
}
</style>
